/**
 * /downhole/templates/:templateId — one BHA template and its slots, on its own
 * page (`GET /templates/:id`). This was a panel beside the Templates list; it
 * moved here when split panes were banned.
 *
 * A template has no edit route. Its actions, on the band: Build an assembly
 * from this (an active template only — the API answers 409 for an archived
 * one), and Archive / Restore (api#902) for every build role, which is every
 * role that can open this page. Archive sets is_active false; assemblies built
 * from it keep the pointer.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, Hammer, Loader2 } from 'lucide-react';
import { getApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useAuth } from '../../contexts/AuthContext';
import { useDownholeTemplate, useSetTemplateActive } from '../../hooks/useDownhole';
import { DH_BHA_TYPE_LABEL, DH_CATEGORY_LABEL, dhLabel } from '../../lib/downhole-labels';
import { canBuildDownhole } from '../../lib/downhole-roles';
import { formatDh } from '../../lib/downhole-units';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import type { DhTemplateDetail } from '../../types/downhole';
import { DH_BAND_SECONDARY, DH_SECONDARY, DhDialog, DhEmpty, DhPanel } from './DownholeBits';
import { ArchivedPill } from './TemplatesScreen';
import { useBackToList } from './recordPage';

const LIST = '/downhole/templates';

function TemplateActions({ t }: { t: DhTemplateDetail }) {
  const { user } = useAuth();
  const canBuild = canBuildDownhole(user?.role);
  const setActive = useSetTemplateActive();
  const [confirmArchive, setConfirmArchive] = useState(false);

  return (
    <div className="flex flex-wrap gap-2">
      {canBuild &&
        (t.is_active ? (
          <button type="button" className={DH_BAND_SECONDARY} disabled={setActive.isPending} onClick={() => setConfirmArchive(true)}>
            <Archive className="size-4" aria-hidden="true" />
            Archive
          </button>
        ) : (
          <button
            type="button"
            className={DH_BAND_SECONDARY}
            disabled={setActive.isPending}
            onClick={async () => {
              try {
                await setActive.mutateAsync({ id: t.id, active: true });
                toast.success(`“${t.name}” restored`);
              } catch (error) {
                toast.error(getApiErrorMessage(error, 'The template was not restored.'));
              }
            }}
          >
            <ArchiveRestore className="size-4" aria-hidden="true" />
            Restore
          </button>
        ))}
      {t.is_active && (
        <Link to={`/downhole/assemblies/new/build?template=${t.id}`} className="fi-action fi-action--standard fi-action--primary">
          <Hammer className="size-4" aria-hidden="true" />
          Build an assembly from this
        </Link>
      )}
      <DhDialog
        open={confirmArchive}
        title={`Archive “${t.name}”?`}
        onClose={() => setConfirmArchive(false)}
        footer={
          <>
            <button type="button" className={DH_SECONDARY} onClick={() => setConfirmArchive(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50"
              disabled={setActive.isPending}
              onClick={async () => {
                try {
                  await setActive.mutateAsync({ id: t.id, active: false });
                  toast.success(`“${t.name}” archived`);
                  setConfirmArchive(false);
                } catch (error) {
                  toast.error(getApiErrorMessage(error, 'The template was not archived.'));
                }
              }}
            >
              Archive
            </button>
          </>
        }
      >
        <p className="text-sm text-fieldiq-text">
          It stops being offered for new builds. Assemblies already built from it keep it, and it can be restored at any time.
        </p>
      </DhDialog>
    </div>
  );
}

export default function TemplateDetailScreen() {
  const { templateId } = useParams<{ templateId: string }>();
  const back = useBackToList(LIST);
  const query = useDownholeTemplate(templateId);
  const failure = queryLoadFailure(query);
  const t = query.data?.data ?? null;
  const units = query.data?.meta.unit_system ?? null;

  return (
    <div data-testid="downhole-template-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Template"
        title={t?.name ?? (query.isLoading ? 'Loading…' : 'Template')}
        onBack={back}
        backLabel="Back to templates"
        actions={t ? <TemplateActions t={t} /> : undefined}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <DhPanel eyebrow="BHA pattern" title="Slots, top to bottom" testId="downhole-template-detail">
            {/* A failed BACKGROUND refetch keeps the template and says so. */}
            {!t && query.isLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading template" />
              </div>
            ) : !t && failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this template" />
            ) : !t ? (
              <DhEmpty>This template does not exist.</DhEmpty>
            ) : (
              <div>
                {failure && (
                  <p role="status" className="border-b border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
                    This template could not be refreshed just now, so it shows what loaded earlier.
                  </p>
                )}
                {!t.is_active && (
                  <p className="flex items-center gap-2 border-b border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                    <ArchivedPill /> Restore it to build from it.
                  </p>
                )}
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3 p-4 text-sm lg:grid-cols-5">
                  <div>
                    <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">BHA type</dt>
                    <dd className="text-white">{dhLabel(DH_BHA_TYPE_LABEL, t.bha_type)}</dd>
                  </div>
                  <div>
                    <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Hole size</dt>
                    <dd className="font-mono text-white">{formatDh(t.hole_size, 'diameter', units)}</dd>
                  </div>
                  <div>
                    <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Customer</dt>
                    <dd className="text-white">{displayValue(t.customer_name)}</dd>
                  </div>
                  <div>
                    <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Well type</dt>
                    <dd className="text-white">{displayValue(t.well_type)}</dd>
                  </div>
                  <div className="col-span-2 lg:col-span-1">
                    <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Formation</dt>
                    <dd className="text-white">{displayValue(t.formation)}</dd>
                  </div>
                  {t.description && <p className="col-span-2 text-fieldiq-text lg:col-span-5">{t.description}</p>}
                </dl>
                <ol className="divide-y divide-fieldiq-border border-t border-fieldiq-border">
                  {t.slots.map((s) => (
                    <li key={s.slot_number} className="flex items-start gap-3 px-4 py-2.5 text-sm">
                      <span className="w-6 text-right font-mono text-fieldiq-muted">{s.slot_number}</span>
                      <span className="min-w-0 flex-1">
                        <span className="font-bold text-white">{s.label || dhLabel(DH_CATEGORY_LABEL, s.category)}</span>
                        <span className="ml-2 text-xs text-fieldiq-muted">
                          {dhLabel(DH_CATEGORY_LABEL, s.category)}
                          {s.required ? ' · required' : ' · optional'}
                        </span>
                        {s.notes && <span className="block text-xs text-fieldiq-text">{s.notes}</span>}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </DhPanel>
        </div>
      </section>
    </div>
  );
}
